/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import Moment from 'react-moment';
import moment from 'moment';
import { formatRoute } from 'react-router-named-routes';
import { withTranslation } from 'react-i18next';
import 'moment/locale/es';
// @material-ui/core components
import userProfileStyles from 'assets/jss/material-dashboard-pro-react/views/userProfileStyles';
import withStyles from '@material-ui/core/styles/withStyles';
// material-ui icons
import Publish from '@material-ui/icons/CloudUpload';
import Edit from '@material-ui/icons/Edit';
import Refresh from '@material-ui/icons/Refresh';
import Visibility from '@material-ui/icons/Visibility';
// global variables
import {
  adStatus,
  paidAdStatus,
  productionUrl,
  promotedAdPackagesStatus,
} from 'config';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Timeline from 'components/UI/Timeline/Timeline';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import CardBody from 'components/UI/Card/CardBody';
import Card from 'components/UI/Card/Card';
// our components
import { AD, PROMOTED_AD, USER, PROMOTED_AD_PACKAGES } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import AdUser from 'components/common/AdUser';
import AdInfo from 'components/common/AdInfo';
import AdInfoSidebar from 'components/common/AdInfoSidebar';
import { LoadingAnimation } from 'components/common/Loading';
import WithPermission from 'components/User/WithPermission';
import SectionDivider from 'components/common/SectionDivider';
import Snackbar from 'components/UI/Snackbar/Snackbar';
import qs from 'helpers/queryStrings';

const ReadableDate = ({ years, months, days, hours, minutes, seconds }) =>
  moment()
    .add({ seconds, minutes, hours, days, months, years })
    .fromNow(true);

/**
 * @class
 * @augments React.Component
 * @description PaidAdDetails component
 * @returns {React.Component} - Returns a component.
 */
class PaidAdDetails extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      logsTab: false,
    };
  }

  /**
   * @description
   * Redirect to ads/details route.
   * @param {number} id - Ad id.
   */
  handleReadAd = id => {
    const history = this.context;
    history.push(formatRoute(AD.DETAILS, { id }));
  };

  /**
   * @description
   * Redirect to package list route.
   * @param {number} id - Package id.
   */
  handleReadPackage = id => {
    const history = this.context;
    const stringified = qs.stringify({
      id,
      status: Object.values(promotedAdPackagesStatus),
    });
    history.push({
      pathname: PROMOTED_AD_PACKAGES.LIST,
      search: stringified,
    });
  };

  /**
   * @description
   * Redirect to paidad/create route.
   */
  handleCreatePaidAd = () => {
    const history = this.context;
    history.push(PROMOTED_AD.CREATE);
  };

  /**
   * @description
   * Redirect to user/details route.
   * @param {number} id - User id.
   */
  handleReadUser = id => {
    const history = this.context;
    history.push(formatRoute(USER.DETAILS, { id }));
  };

  /**
   * @description
   * Redirect to paidads/update route.
   */
  handlePaidAdUpdate = () => {
    const { data } = this.props;
    const history = this.context;
    history.push(formatRoute(PROMOTED_AD.UPDATE, { id: data.paidAd.id }));
  };

  /**
   * @description
   * Switch menu tabs.
   *
   * @param {string} tab - Tab to switch.
   */
  handleTab = tab => {
    if (tab === 'logs') {
      this.setState({ logsTab: true });
    } else this.setState({ logsTab: false });
  };

  /**
   * @description
   * Get refund status.
   *
   * @param {Object} param - Paid ad.
   * @param {Function} paymentRefund - Mutation for refund.
   * @param {Function} t - Translation.
   * @returns {Object} Action for the refund status.
   */
  getRefundStatus = ({ paidAd }, paymentRefund, t) => {
    const totalRefundButton = (
      <Button
        data-cy="refundTotalButton"
        simple
        color="danger"
        id="refundTotal"
        onClick={() =>
          paymentRefund({
            autoPaidAd: paidAd.id,
            total: true,
          })
        }
      >
        <span>{t('common.refund_total')}</span>
      </Button>
    );
    const partialRefundButton = (
      <Button
        data-cy="refundPartialButton"
        simple
        color="danger"
        id="refundPartial"
        onClick={() =>
          paymentRefund({
            autoPaidAd: paidAd.id,
            total: false,
          })
        }
      >
        <span>{t('common.refund_partial')}</span>
      </Button>
    );

    switch (paidAd.refundStatus) {
      case 'PENDING_REFUND':
        return <span>{t('common.refund_pending')}</span>;
      case 'TOTAL_REFUNDED':
        return <span>{t('common.refund_total_completed')}</span>;
      case 'PARTIAL_REFUNDED':
        return <span>{t('common.refund_total_completed')}</span>;
      case 'REFUNDED':
        return <span>{t('common.refund_total_completed')}</span>;
      default:
        if (paidAd.netSale > 0) {
          return (
            <React.Fragment>
              {totalRefundButton}
              {partialRefundButton}
            </React.Fragment>
          );
        }
        return <span>{t('common.refund_cannot_refund')}</span>;
    }
  };

  renderOtherActions = () => {
    const {
      t,
      classes,
      data,
      paidAdExtend,
      paidAdPause,
      paidAdCancel,
      paidAdResume,
      paymentRefund,
      paidAdActionLoading,
    } = this.props;

    if (data.paidAd.status !== paidAdStatus.CANCELLED) {
      return (
        <Card>
          {paidAdActionLoading && (
            <div className={classes.actionsLoader}>
              <LoadingAnimation />
            </div>
          )}
          <div className={classes.permissionsContainer}>
            <div>
              <div>
                <h6>Otras acciones</h6>
              </div>
              <div>
                <WithPermission
                  allowPermissions={[
                    'pause_resume_promotedad',
                    'pause_resume_ownership_promotedad',
                  ]}
                >
                  {data.paidAd.status !== paidAdStatus.COMPLETED &&
                    ([paidAdStatus.PAUSED, paidAdStatus.NO_CREDIT].includes(
                      data.paidAd.status,
                    ) ? (
                      <Button
                        data-cy="paidadResumeButton"
                        simple
                        color="primary"
                        id="paidads-resume"
                        onClick={() => paidAdResume([data.paidAd.id])}
                      >
                        <span>{t('common.resume')}</span>
                      </Button>
                    ) : (
                      <Button
                        data-cy="paidadPauseButton"
                        simple
                        color="primary"
                        id="paidads-pause"
                        onClick={() => paidAdPause([data.paidAd.id])}
                      >
                        <span>{t('common.pause')}</span>
                      </Button>
                    ))}
                </WithPermission>
                <WithPermission
                  allowPermissions={[
                    'extend_ownership_promotedad',
                    'extend_promotedad',
                  ]}
                >
                  <Button
                    data-cy="paidadExtendButton"
                    simple
                    color="success"
                    id="paidads-extend"
                    onClick={() => paidAdExtend([data.paidAd.id])}
                  >
                    <span>{t('common.extend')}</span>
                  </Button>
                </WithPermission>
                <WithPermission
                  allowPermissions={[
                    'cancel_promotedad',
                    'cancel_ownership_promotedad',
                  ]}
                >
                  {data.paidAd.status !== paidAdStatus.COMPLETED && (
                    <Button
                      data-cy="paidadCancelButton"
                      simple
                      color="danger"
                      id="paidads-cancel"
                      onClick={() => paidAdCancel([data.paidAd.id])}
                    >
                      <span>{t('common.cancel')}</span>
                    </Button>
                  )}
                </WithPermission>
              </div>
            </div>
          </div>
        </Card>
      );
    }
    return (
      data.paidAd.ad &&
      data.paidAd.ad.status !== adStatus.EXPIRED && (
        <Card>
          {paidAdActionLoading && (
            <div className={classes.actionsLoader}>
              <LoadingAnimation />
            </div>
          )}
          <div className={classes.permissionsContainer}>
            <div>
              <div>
                <h6>Otras acciones</h6>
              </div>
              <div>
                <WithPermission
                  allowPermissions={['add_admincredittransaction']}
                >
                  {this.getRefundStatus(data, paymentRefund, t)}
                </WithPermission>
              </div>
            </div>
          </div>
        </Card>
      )
    );
  };

  /**
   * @description
   * Render Details Ad.
   */
  render() {
    const { logsTab } = this.state;
    const {
      classes,
      data,
      adReview,
      adPublish,
      adActionLoading,
      refresh,
      t,
      errors,
      closeNotification,
    } = this.props;
    const canShowActions =
      data.paidAd.ad && data.paidAd.ad.status !== adStatus.EXPIRED;

    return (
      <React.Fragment>
        <Snackbar
          place="tc"
          color="danger"
          message={errors.length > 0 && errors[0].messages[0]}
          open={errors.length > 0}
          closeNotification={closeNotification}
          close
        />
        <GridContainer>
          <GridItem xs={12} sm={12} md={6}>
            <Button
              data-cy="details-tab"
              color="primary"
              simple={logsTab}
              round
              id="details-tab"
              className={classes.logsBtn}
              onClick={() => this.handleTab('ads-details')}
            >
              {t('promoted_ad.details.section')}
              <span>{t('ad.details.section_break')}</span>
            </Button>

            <Button
              data-cy="logs-tab"
              color="primary"
              simple={!logsTab}
              round
              id="logs-tab"
              onClick={() => this.handleTab('logs')}
            >
              {t('ad.details.tabs.log')}
            </Button>
          </GridItem>
          <GridItem xs={12} sm={12} md={6}>
            <GridContainer justify="flex-end" alignItems="center">
              <GridItem>
                <Button
                  color="primary"
                  simple
                  className={classes.showFilters}
                  onClick={() => refresh()}
                  id="refresh"
                  key="refresh"
                >
                  <Refresh />
                  {t('common.refresh')}
                </Button>
              </GridItem>
              <WithPermission
                allowPermissions={[
                  'add_promotedad',
                  'add_ownership_promotedad',
                ]}
              >
                <GridItem>
                  <Button
                    color="success"
                    id="createPaidAd"
                    className={classes.logsBtn}
                    onClick={() => this.handleCreatePaidAd()}
                  >
                    {t('promoted_ad.create')}
                  </Button>
                </GridItem>
              </WithPermission>
            </GridContainer>
          </GridItem>
        </GridContainer>
        <GridContainer>
          {logsTab ? (
            <GridItem xs={12} sm={12} md={8} lg={8}>
              <Timeline data-cy="logs" stories={data.logs} />
            </GridItem>
          ) : (
            <GridItem xs={12} sm={12} md={8} lg={8}>
              <Card>
                <CardBody>
                  <SectionDivider label={t('promoted_ad.details.ad_section')}>
                    <GridContainer>
                      <GridItem xs={12} sm={5} md={5} lg={5} xl={2}>
                        <div className={classes.userDetails}>
                          <h6>{t('promoted_ad.details.status')}</h6>
                          <p>{t(`promoted_ad.status.${data.paidAd.status}`)}</p>
                        </div>
                        <div className={classes.userDetails}>
                          <h6>{t('promoted_ad.details.id_promoted')}</h6>
                          <p id={`paidAd-${data.paidAd.id}`}>
                            {data.paidAd.id}
                          </p>
                        </div>
                        <div className={classes.userDetails}>
                          <h6>{t('promoted_ad.details.id_ad')}</h6>
                          <WithPermission allowPermissions={['view_ad']}>
                            <Button
                              id="ads-details"
                              simple
                              color="primary"
                              style={{ padding: '0' }}
                              onClick={() => {
                                this.handleReadAd(data.paidAd.ad.id);
                              }}
                            >
                              {data.paidAd.ad && data.paidAd.ad.id}
                            </Button>
                          </WithPermission>
                        </div>
                        <div className={classes.userDetails}>
                          <h6>{t('promoted_ad.details.configuration')}</h6>
                          <WithPermission
                            allowPermissions={['list_promotedadpackage']}
                            alt={
                              <p id={`paidAd-${data.paidAd.package.id}`}>
                                {data.paidAd.package.id}
                              </p>
                            }
                          >
                            <Button
                              id="package-list"
                              simple
                              color="primary"
                              style={{ padding: '0' }}
                              onClick={() => {
                                this.handleReadPackage(data.paidAd.package.id);
                              }}
                            >
                              {data.paidAd.package.id}
                            </Button>
                          </WithPermission>
                        </div>
                        <div className={classes.userDetails}>
                          <h6>
                            {t('promoted_ad.filters.location_scope.title')}
                          </h6>
                          <p>
                            {t(
                              `promoted_ad.filters.location_scope.${data.paidAd.locationScope.toLowerCase()}`,
                            )}
                          </p>
                        </div>
                        <div className={classes.userDetails}>
                          <h6>{t('promoted_ad.filters.duration')}</h6>
                          <p>{ReadableDate(data.paidAd.duration)}</p>
                        </div>
                        <div className={classes.userDetails}>
                          <div style={{ display: 'flex' }}>
                            <div style={{ marginRight: '50px' }}>
                              <h6>{t('promoted_ad.details.price')}</h6>
                              <p>
                                {data.paidAd.userPrice} {data.paidAd.currency}
                              </p>
                            </div>
                            <div>
                              <h6>{t('promoted_ad.details.net_price')}</h6>
                              <p>
                                {data.paidAd.netSale} {data.paidAd.currency}
                              </p>
                            </div>
                          </div>
                        </div>
                        {data.paidAd.promoType && (
                          <div className={classes.userDetails}>
                            <h6>{t('paidad.details.promotion.title')}</h6>
                            <p>
                              {t(
                                `promoted_ad.details.discount_types.${data.paidAd.promoType.toLowerCase()}`,
                              )}{' '}
                              (
                              {data.paidAd.promotionRelated ? (
                                <a
                                  rel="noopener noreferrer"
                                  target="_blank"
                                  href={`${PROMOTED_AD.LIST}?promotedBundle=${data.paidAd.promotionRelated}`}
                                  className={classes.adLinks}
                                >
                                  {data.paidAd.promotionRelated}
                                </a>
                              ) : (
                                ''
                              )}
                              )
                            </p>
                          </div>
                        )}
                        <div className={classes.userDetails}>
                          <h6>{t('promoted_ad.details.consumed_time')}</h6>
                          <p>{data.paidAd.consumedTime}%</p>
                        </div>
                        <div className={classes.userDetails}>
                          <h6>{t('promoted_ad.details.refunded_amount')}</h6>
                          <p>{data.paidAd.refundedAmount || 0}</p>
                        </div>
                        <WithPermission allowPermissions={[]}>
                          <div className={classes.userDetails}>
                            <h6>{t('promoted_ad.details.consumed_credit')}</h6>
                            <p>{data.paidAd.consumedCredit || 0}</p>
                          </div>
                        </WithPermission>
                      </GridItem>
                      <GridItem
                        xs={12}
                        sm={7}
                        md={7}
                        lg={7}
                        xl={10}
                        style={{ marginTop: '15px' }}
                      >
                        <div>
                          <h6>{t('promoted_ad.details.created_on')}</h6>
                          <Moment
                            date={data.paidAd.createdOn}
                            format="LLLL"
                            fromNow
                          />
                        </div>
                        <div>
                          <h6>{t('promoted_ad.details.start_date')}</h6>
                          <Moment
                            date={data.paidAd.startDate}
                            format="LLLL"
                            fromNow
                          />
                        </div>
                        <div className={classes.userDetails}>
                          <h6>{t('promoted_ad.details.end_date')}</h6>
                          <Moment
                            date={data.paidAd.endDate}
                            format="LLLL"
                            fromNow
                          />
                        </div>
                        {data.paidAd.manager && (
                          <div className={classes.userDetails}>
                            <h6>{t('promoted_ad.details.manager')}</h6>
                            {data.paidAd.manager.email ? (
                              <WithPermission
                                alt={
                                  data.paidAd.manager && (
                                    <p>
                                      {data.paidAd.manager.fullname} •
                                      {data.paidAd.manager.email}
                                    </p>
                                  )
                                }
                                allowPermissions={['view_agency']}
                              >
                                <p>
                                  <Button
                                    id="user-details"
                                    simple
                                    style={{ padding: '0' }}
                                    color="primary"
                                    onClick={() => {
                                      this.handleReadUser(
                                        data.paidAd.manager.id,
                                      );
                                    }}
                                  >
                                    {data.paidAd.manager.fullname}
                                  </Button>
                                  • {data.paidAd.manager.email}
                                </p>
                              </WithPermission>
                            ) : (
                              <WithPermission
                                alt={
                                  data.paidAd.manager && (
                                    <p>{data.paidAd.manager.fullname}</p>
                                  )
                                }
                                allowPermissions={['view_agency']}
                              >
                                <Button
                                  id="user-details"
                                  simple
                                  style={{ padding: '0' }}
                                  color="primary"
                                  onClick={() => {
                                    this.handleReadUser(data.paidAd.manager.id);
                                  }}
                                >
                                  {data.paidAd.manager &&
                                    data.paidAd.manager.fullname}
                                </Button>
                              </WithPermission>
                            )}
                          </div>
                        )}
                        <div className={classes.userDetails}>
                          <h6>
                            {data.paidAd.owner && data.paidAd.owner.email
                              ? t('promoted_ad.details.owner')
                              : t('promoted_ad.form.fields.user')}
                          </h6>
                          {data.paidAd.owner &&
                            !data.paidAd.owner.fullname &&
                            `${t('promoted_ad.form.fields.private_user')} (${
                              data.paidAd.owner.fakeId
                            })`}
                          {data.paidAd.owner && data.paidAd.owner.email ? (
                            <WithPermission
                              alt={
                                data.paidAd.owner && (
                                  <p>
                                    {data.paidAd.owner.fullname} •
                                    {data.paidAd.owner.email}
                                  </p>
                                )
                              }
                              allowPermissions={[
                                'view_ownership_useragency',
                                'view_useragency',
                              ]}
                            >
                              <p>
                                <Button
                                  id="user-details"
                                  simple
                                  style={{ padding: '0' }}
                                  color="primary"
                                  onClick={() => {
                                    this.handleReadUser(data.paidAd.owner.id);
                                  }}
                                >
                                  {data.paidAd.owner.fullname}
                                </Button>
                                • {data.paidAd.owner.email}
                              </p>
                            </WithPermission>
                          ) : (
                            <WithPermission
                              alt={
                                data.paidAd.owner && (
                                  <p>{data.paidAd.owner.fullname}</p>
                                )
                              }
                              allowPermissions={[
                                'view_ownership_useragency',
                                'view_useragency',
                              ]}
                            >
                              <Button
                                id="user-details"
                                simple
                                style={{ padding: '0' }}
                                color="primary"
                                onClick={() => {
                                  this.handleReadUser(data.paidAd.owner.id);
                                }}
                              >
                                {data.paidAd.owner &&
                                  data.paidAd.owner.fullname}
                              </Button>
                            </WithPermission>
                          )}
                        </div>
                        {data.paidAd.lead && (
                          <div className={classes.userDetails}>
                            <h6>{t('promoted_ad.details.lead')}</h6>
                            <p>{data.paidAd.lead}</p>
                          </div>
                        )}
                        {data.paidAd.ad && data.paidAd.ad.reviewed && (
                          <div className={classes.userDetails}>
                            <h6>{t('promoted_ad.details.reviewed')}</h6>
                            <p>
                              <span>
                                {data.paidAd.ad.reviewedBy &&
                                  data.paidAd.ad.reviewedBy.fullname}{' '}
                                •{' '}
                              </span>
                              <Moment
                                date={data.paidAd.ad.reviewedOn}
                                format="LLLL"
                                fromNow
                              />
                            </p>
                          </div>
                        )}
                      </GridItem>
                      <GridItem>
                        {data.paidAd.ad && (
                          <div className={classes.userDetails}>
                            <h6>{t('promoted_ad.details.url')}</h6>
                            <a
                              rel="noopener noreferrer"
                              target="_blank"
                              href={productionUrl + data.paidAd.ad.permalink}
                              className={classes.adLinks}
                            >
                              {productionUrl + data.paidAd.ad.permalink}
                            </a>
                          </div>
                        )}
                      </GridItem>
                    </GridContainer>
                    {data.paidAd.notes && (
                      <GridContainer>
                        <GridItem xs={12} sm={10} md={10} lg={10} xl={10}>
                          <div className={classes.userDetails}>
                            <h6>{t('promoted_ad.details.notes')}</h6>
                            <p>{data.paidAd.notes}</p>
                          </div>
                        </GridItem>
                      </GridContainer>
                    )}
                  </SectionDivider>
                </CardBody>
              </Card>
              {data.paidAd.ad && <AdInfo data={data.paidAd} />}
            </GridItem>
          )}
          <GridItem xs={12} sm={12} md={4} lg={4}>
            {canShowActions && (
              <Card>
                {adActionLoading && (
                  <div className={classes.actionsLoader}>
                    <LoadingAnimation />
                  </div>
                )}
                <div className={classes.permissionsContainer}>
                  {data.paidAd.status === paidAdStatus.PENDING &&
                    data.paidAd.ad.isPublished &&
                    !data.paidAd.ad.reviewed && (
                      <WithPermission allowPermissions={['review_ad']}>
                        <Button
                          data-cy="adReviewButton"
                          color="success"
                          id="ads-review"
                          onClick={() => adReview([data.paidAd.ad.id])}
                        >
                          <Visibility />
                          <span>{t('common.review')}</span>
                        </Button>
                      </WithPermission>
                    )}
                  {data.paidAd.status === paidAdStatus.PENDING &&
                    !data.paidAd.ad.isPublished && (
                      <WithPermission allowPermissions={['publish_ad']}>
                        <Button
                          data-cy="adPublishButton"
                          color="primary"
                          id="ads-publish"
                          onClick={() => adPublish([data.paidAd.ad.id])}
                        >
                          <Publish />
                          <span>{t('common.publish')}</span>
                        </Button>
                      </WithPermission>
                    )}
                  {data.paidAd.status !== paidAdStatus.CANCELLED && (
                    <WithPermission
                      allowPermissions={[
                        'change_promotedad',
                        'change_ownership_promotedad',
                      ]}
                    >
                      <Button
                        data-cy="adUpdateButton"
                        simple
                        color="primary"
                        id="ads-update"
                        onClick={this.handlePaidAdUpdate}
                      >
                        <Edit />
                        <span>{t('common.edit')}</span>
                      </Button>
                    </WithPermission>
                  )}
                </div>
              </Card>
            )}

            {this.renderOtherActions()}

            {data.paidAd.ad && (
              <React.Fragment>
                <AdInfoSidebar data={data.paidAd} />
                <AdUser data={data.paidAd} />
              </React.Fragment>
            )}
          </GridItem>
        </GridContainer>
      </React.Fragment>
    );
  }
}

/**
 * PaidAdDetails PropTypes
 * @param classes - styles
 * @param data - ad query's results
 * @param t - prop from HOC with string translations
 * @param i18n - prop from HOC with string translations
 * @param errors - prop errors list received from parents
 * @callback adReview - callback to review elements
 * @callback adPublish - callback to publish elements
 * @callback paidAdExtend - callback to extend paidad
 * @callback paidAdPause - callback to pause paidad
 * @callback paidAdResume - callback to resume paidad
 * @callback paidAdCancel - callback to cancel paidad
 * @callback paidAdRunTask - callback to run paid ad task
 * @callback paymentRefund - callback to refund payment
 * @callback adActionLoading - boolean to show loading when user taps on action
 * @callback paidAdActionLoading - boolean to show loading when user taps action
 * @callback refresh - callback to refresh page
 * @callback closeNotification - callback to close notification
 */
PaidAdDetails.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  data: PropTypes.shape({}).isRequired,
  adReview: PropTypes.func.isRequired,
  adPublish: PropTypes.func.isRequired,
  paidAdExtend: PropTypes.func.isRequired,
  paidAdPause: PropTypes.func.isRequired,
  paidAdResume: PropTypes.func.isRequired,
  paidAdCancel: PropTypes.func.isRequired,
  paymentRefund: PropTypes.func.isRequired,
  adActionLoading: PropTypes.bool.isRequired,
  paidAdActionLoading: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,
  errors: PropTypes.instanceOf(Array).isRequired,
  closeNotification: PropTypes.func.isRequired,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to AdDetails component
// This is a small hack, with intermediary Component to resolved it temporally
const PaidAdDetailsWrapper = props => <PaidAdDetails {...props} />;

export default withTranslation()(
  withStyles(userProfileStyles)(PaidAdDetailsWrapper),
);
